import axios from "axios"
import { getUserData } from "@src/utility/Utils"
import { apiConfig } from "@configs/apiConfig"
import * as Sentry from "@sentry/react"

// Usar configuração centralizada
const BASE_URL = apiConfig.baseURL

// Create axios instance for authentication (without auth interceptor)
const authApi = axios.create({
  baseURL: BASE_URL,
  timeout: apiConfig.timeout,
  headers: apiConfig.defaultHeaders
})

// Response interceptor para authApi também capturar erros
authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    // Captura erros HTTP para o Sentry
    if (error.response) {
      const status = error.response.status
      const url = error.config?.url || "unknown"
      const method = error.config?.method?.toUpperCase() || "unknown"

      // Captura erros de servidor (500, 502, 503, 504)
      if (status >= 500) {
        Sentry.captureException(error, {
          tags: {
            errorType: "http_error",
            httpStatus: status,
            httpMethod: method,
            apiType: "auth"
          },
          contexts: {
            request: {
              url,
              method,
              status_code: status,
              headers: error.config?.headers,
              data: error.config?.data
            },
            response: {
              status_code: status,
              headers: error.response.headers,
              data: error.response.data
            }
          },
          extra: {
            requestUrl: url,
            requestMethod: method,
            responseStatus: status,
            responseData: error.response.data
          }
        })
      }
    } else if (error.request) {
      // Erro de rede (sem resposta do servidor)
      Sentry.captureException(error, {
        tags: {
          errorType: "network_error",
          apiType: "auth"
        },
        contexts: {
          request: {
            url: error.config?.url || "unknown",
            method: error.config?.method?.toUpperCase() || "unknown"
          }
        }
      })
    }

    return Promise.reject(error)
  }
)

// Create axios instance
const api = axios.create({
  baseURL: BASE_URL,
  timeout: apiConfig.timeout,
  headers: apiConfig.defaultHeaders
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const userData = getUserData()

    if (userData && userData.accessToken) {
      config.headers.Authorization = `Bearer ${userData.accessToken}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Captura erros HTTP para o Sentry
    if (error.response) {
      const status = error.response.status
      const url = error.config?.url || "unknown"
      const method = error.config?.method?.toUpperCase() || "unknown"

      // Captura erros de servidor (500, 502, 503, 504)
      if (status >= 500) {
        Sentry.captureException(error, {
          tags: {
            errorType: "http_error",
            httpStatus: status,
            httpMethod: method
          },
          contexts: {
            request: {
              url,
              method,
              status_code: status,
              headers: error.config?.headers,
              data: error.config?.data
            },
            response: {
              status_code: status,
              headers: error.response.headers,
              data: error.response.data
            }
          },
          extra: {
            requestUrl: url,
            requestMethod: method,
            responseStatus: status,
            responseData: error.response.data
          }
        })
      }
    } else if (error.request) {
      // Erro de rede (sem resposta do servidor)
      Sentry.captureException(error, {
        tags: {
          errorType: "network_error"
        },
        contexts: {
          request: {
            url: error.config?.url || "unknown",
            method: error.config?.method?.toUpperCase() || "unknown"
          }
        }
      })
    }

    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || ""
      const isLoginRequest = requestUrl.includes("/auth/login")

      // Verifica se é uma requisição para nossa API
      // Se não começar com http/https, é relativa (então é nossa)
      // Se começar com http/https, deve conter nossa baseURL
      const isBackendRequest =
        !requestUrl.match(/^https?:\/\//) ||
        requestUrl.includes(apiConfig.baseURL)

      // Só faz logout se for requisição para o backend e não for login
      if (!isLoginRequest && isBackendRequest) {
        localStorage.removeItem("userData")
        window.location.href = "/login"
      }
    }
    return Promise.reject(error)
  }
)

// Auth Services (using authApi without auth interceptor)
export const authService = {
  login: (credentials) => authApi.post("/auth/login", credentials),
  register: (userData) => authApi.post("/auth/register", userData),
  logout: () => api.post("/auth/logout"),
  refreshToken: (refreshToken) =>
    authApi.post("/auth/refresh", { refreshToken }),
  forgotPassword: (email) => authApi.post("/auth/forgotPassword", { email }),
  verifyPasswordResetCode: (data) =>
    authApi.post("/auth/verifyPasswordResetCode", data),
  resetPassword: (data) => authApi.post("/auth/resetPassword", data),
  verifyEmail: (token) => authApi.post("/auth/verify-email", { token }),
  resendVerification: (email) =>
    authApi.post("/auth/resend-verification", { email }),
  // Novos endpoints para o fluxo de autocadastro
  verifyAccount: (data) => authApi.post("/auth/verifyAccount", data),
  resendVerificationCode: (email) =>
    authApi.post("/auth/resendVerification", { email }),
  googleLogin: (token) => authApi.post("/auth/google", { token }),
  googleRegister: (token) => authApi.post("/auth/google/register", { token })
}

// Profile Services
export const profileService = {
  // Obter perfil do usuário autenticado
  getProfile: () => api.get("/profile"),

  // Atualizar perfil
  updateProfile: (profileData) => api.put("/profile", profileData),

  // Upload de avatar
  uploadAvatar: (formData) => {
    return api.post("/profile/uploadAvatar", formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    })
  },

  // Remover avatar
  removeAvatar: () => api.delete("/profile/removeAvatar"),

  // Alterar senha
  changePassword: (passwordData) =>
    api.post("/profile/changePassword", passwordData),

  // Estatísticas do perfil
  getProfileStats: () => api.get("/profile/stats")
}

// Case Services
export const caseService = {
  // Listar casos por filtro
  getAll: (filters = {}) => {
    const params = new URLSearchParams()
    if (filters.status && filters.status !== "all")
      params.append("status", filters.status)
    if (filters.search) params.append("search", filters.search)
    if (filters.page) params.append("page", filters.page)
    if (filters.per_page) params.append("per_page", filters.per_page)
    if (filters.limit) params.append("limit", filters.limit)
    return api.get(`/cases?${params.toString()}`)
  },

  // Obter caso específico
  getById: (id) => api.get(`/cases/${id}`),

  // Criar novo caso
  create: (caseData) => api.post("/cases", caseData),

  // Atualizar caso
  update: (id, caseData) => api.put(`/cases/${id}`, caseData),

  // Deletar caso
  delete: (id) => api.delete(`/cases/${id}`),

  // Arquivar caso
  archive: (id) => api.post(`/cases/${id}/archive`, { archive: true }),

  // Desarquivar caso
  unarchive: (id) => api.post(`/cases/${id}/unarchive`, { archive: false }),

  // Avançar caso para próximo status
  nextFlow: (id, payload) => api.post(`/cases/${id}/next`, payload),

  // Avançar caso para próximo status (endpoint específico)
  nextStatus: (id) => api.post(`/cases/${id}/nextStatus`),
  // Transição de status com validações
  transition: (id, payload) => api.post(`/cases/${id}/transition`, payload),
  confirmServicePlanning: (caseId, serviceId, message) =>
    api.post(`/cases/${caseId}/services/${serviceId}/confirm-planning`, {
      message
    }),

  // Atualizar dados do paciente
  updatePatient: (id, patient) => api.post(`/cases/${id}/patient`, patient),

  // Definir dados do paciente (endpoint específico)
  setPatient: (id, patient) => api.post(`/cases/${id}/setPatient`, patient),

  // Atualizar observações
  updateObservation: (id, observation) =>
    api.post(`/cases/${id}/observation`, { observation }),

  // Atualizar dados do serviço
  updateServiceData: (id, serviceId, data) =>
    api.post(`/cases/${id}/service/${serviceId}/data`, data),

  // Marcar caso como resolvido
  markSolved: (reviewId) => api.post(`/cases/review/${reviewId}/solve`),

  // Anexos
  getAttachments: (id) => api.get(`/cases/${id}/attachments`),
  getBuckets: (caseId) => api.get(`/cases/${caseId}/buckets`),
  getBucketAttachments: (caseId, bucket, page = 1, perPage = 10) =>
    api.get(`/cases/${caseId}/buckets/${encodeURIComponent(bucket)}?page=${page}&per_page=${perPage}`),
  toggleBucketVisibility: (caseId, bucket, visibility) =>
    api.post(`/cases/${caseId}/buckets/${encodeURIComponent(bucket)}/visibility`, { visibility }),
  addAttachment: (id, attachment) =>
    api.post(`/cases/${id}/attachments`, attachment),
  deleteAttachment: (id, attachmentId) =>
    api.delete(`/cases/${id}/attachments/${attachmentId}`),

  // Upload de anexos (usado pelo uploadService)
  uploadAttachment: (file, metadata = {}) => {
    const formData = new FormData()
    formData.append("file", file)

    // Adicionar metadados
    if (metadata.case_id) formData.append("case_id", metadata.case_id)
    if (metadata.category) formData.append("category", metadata.category)
    if (metadata.description)
      formData.append("description", metadata.description)

    return api.post("/attachment/uploadSync", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
  },

  // Completar upload multipart
  completeMultipartUpload: (attachmentId, uploadId, parts) => {
    return api.post(`/attachment/${attachmentId}/complete-multipart`, {
      upload_id: uploadId,
      parts
    })
  },

  // Abortar upload multipart
  abortMultipartUpload: (attachmentId, uploadId) => {
    return api.post(`/attachment/${attachmentId}/abort-multipart`, {
      upload_id: uploadId
    })
  },

  // Mensagens/Chat
  getMessages: (id) => api.get(`/cases/${id}/messages`),
  sendMessage: (id, message) => api.post(`/cases/${id}/messages`, message),

  // Casos arquivados
  getArchivedCases: (filters = {}) => {
    const params = new URLSearchParams()
    params.append("archived", "true")
    if (filters.search) params.append("search", filters.search)
    if (filters.page) params.append("page", filters.page)
    if (filters.per_page) params.append("per_page", filters.per_page)
    if (filters.limit) params.append("limit", filters.limit)
    return api.get(`/cases?${params.toString()}`)
  },

  // Novos endpoints para o fluxo de status
  // Validação
  rejectValidation: (id, payload) =>
    api.post(`/cases/${id}/reject-validation`, payload),
  approveValidation: (id) => api.post(`/cases/${id}/approve-validation`),
  resubmitValidation: (id) => api.post(`/cases/${id}/resubmit-validation`),

  // Planejamento
  requestPlanningResponse: (id, payload) =>
    api.post(`/cases/${id}/request-planning-response`, payload),
  respondPlanningRequest: (id, payload) =>
    api.post(`/cases/${id}/respond-planning-request`, payload),

  // Aprovação (por serviço)
  approveServicePlanning: (caseId, serviceId, password) =>
    api.post(`/cases/${caseId}/services/${serviceId}/approve-planning`, {
      password
    }),
  rejectServicePlanning: (caseId, serviceId, message) =>
    api.post(`/cases/${caseId}/services/${serviceId}/reject-planning`, {
      message
    }),

  // Finalização
  finishCase: (id, payload) => api.post(`/cases/${id}/finish`, payload),

  // Cancelamento (admin)
  cancelCase: (id, observation = '') => api.post(`/cases/${id}/cancel`, { observation }),

  // Histórico de status
  getStatusHistory: (id) => api.get(`/cases/${id}/status-history`),

  // Seleção de dentes/arcadas para items
  // Para tipo 2 (Arcada): { arch: "superior" | "inferior" | "both" }
  // Para tipo 3 (Dente): { teeth: [11, 12, 21, 22] }
  updateItemSelection: (caseId, itemId, selection) =>
    api.put(`/cases/${caseId}/items/${itemId}/selection`, selection),

  getStats: () => api.get("/cases/stats"),

  setPlanner: (id, payload) => api.post(`/cases/${id}/setPlanner`, payload),
  setPrinter: (id, payload) => api.post(`/cases/${id}/setPrinter`, payload),
  addCaseUserByEmail: (id, email) => api.post(`/cases/${id}/addUserByEmail`, { email }),
  removeCaseUser: (id, userId) => api.delete(`/cases/${id}/removeUser/${userId}`)
}

// Método para buscar casos
export const getCases = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams()
    if (filters.status && filters.status !== "all")
      queryParams.append("status", filters.status)
    if (filters.search) queryParams.append("search", filters.search)
    if (filters.page) queryParams.append("page", filters.page)
    if (filters.per_page) queryParams.append("per_page", filters.per_page)
    if (filters.limit) queryParams.append("limit", filters.limit)

    const url = `/cases${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`

    const response = await api.get(url)
    return response.data
  } catch (error) {
    throw error
  }
}

// User Services
export const userService = {
  getAll: (filters = {}) => api.get("/users", { params: filters }),
  getById: (id) => api.get(`/users/${id}`),
  create: (userData) => api.post("/users", userData),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  delete: (id) => api.delete(`/users/${id}`),
  updateProfile: (id, profileData) =>
    api.put(`/users/${id}/profile`, profileData),
  updatePassword: (id, passwordData) =>
    api.put(`/users/${id}/password`, passwordData),
  getProfile: () => api.get("/profile"),
  uploadAvatar: (file) => {
    const formData = new FormData()
    formData.append("avatar", file)
    return api.post("/users/avatar", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
  },
  updateRoles: (id, roles) => api.put(`/users/${id}/roles`, { roles }),
  updatePermissions: (id, permissions) =>
    api.put(`/users/${id}/permissions`, { permissions }),
  verify: (id) => api.put(`/users/${id}/verify`),
  invite: (inviteData) => api.post("/users/invite", inviteData),
  bindUser: (id, bindData) => api.post(`/users/${id}/bindUser`, bindData),
  getUserCases: (id, params = {}) => api.get(`/users/${id}/cases`, { params }),
  updateCapabilities: (id, capabilities) =>
    api.put(`/users/${id}/capabilities`, capabilities),
  listByRole: (role) => api.get(`/users/list-by-role?role=${role}`)
}

// Product Services
export const productService = {
  getAll: (filters = {}) => {
    // Construir parâmetros de query para filtros
    const params = new URLSearchParams()

    // Filtro por nome/pesquisa
    if (filters.search) {
      params.append("search", filters.search)
    }

    // Filtro por categoria
    if (filters.categoryId) {
      params.append("categoryId", filters.categoryId)
    }

    // Filtro por tipo de produto
    if (filters.productType) {
      params.append("productType", filters.productType)
    }

    // Filtro por tipo de produto (planejamento/manufatura)
    if (filters.productKind) {
      params.append("productKind", filters.productKind)
    }

    // Filtros adicionais
    if (filters.priceMin !== undefined) {
      params.append("priceMin", filters.priceMin)
    }

    if (filters.priceMax !== undefined) {
      params.append("priceMax", filters.priceMax)
    }

    if (filters.inStock !== undefined) {
      params.append("inStock", filters.inStock)
    }

    // Paginação
    if (filters.page) {
      params.append("page", filters.page)
    }

    if (filters.limit) {
      params.append("limit", filters.limit)
    }

    // Ordenação
    if (filters.sortBy) {
      params.append("sortBy", filters.sortBy)
    }

    if (filters.sortOrder) {
      params.append("sortOrder", filters.sortOrder)
    }

    return api.get(`/products?${params.toString()}`)
  },

  // Lista de produtos para administração
  getList: (filters = {}) => {
    // Construir parâmetros de query para filtros
    const params = new URLSearchParams()

    // Filtro por nome/pesquisa
    if (filters.search) {
      params.append("search", filters.search)
    }

    // Filtro por categoria - CORRIGIDO para usar snake_case
    if (filters.category_id) {
      params.append("category_id", filters.category_id)
    }

    // Filtro por tipo de produto - CORRIGIDO para usar snake_case
    if (filters.product_type) {
      params.append("product_type", filters.product_type)
    }

    // Filtro por tipo de produto (planejamento/manufatura) - CORRIGIDO para usar snake_case
    if (filters.product_kind) {
      params.append("product_kind", filters.product_kind)
    }

    // Paginação - CORRIGIDO para usar snake_case
    if (filters.page) {
      params.append("page", filters.page)
    }

    if (filters.per_page) {
      params.append("per_page", filters.per_page)
    }

    // Filtros adicionais
    if (filters.priceMin !== undefined) {
      params.append("priceMin", filters.priceMin)
    }

    if (filters.priceMax !== undefined) {
      params.append("priceMax", filters.priceMax)
    }

    if (filters.inStock !== undefined) {
      params.append("inStock", filters.inStock)
    }

    // Paginação
    if (filters.page) {
      params.append("page", filters.page)
    }

    if (filters.limit) {
      params.append("limit", filters.limit)
    }

    // Ordenação
    if (filters.sortBy) {
      params.append("sortBy", filters.sortBy)
    }

    if (filters.sortOrder) {
      params.append("sortOrder", filters.sortOrder)
    }

    const queryString = params.toString()

    return api.get(`/products/list?${queryString}`)
  },

  getById: (id) => api.get(`/products/${id}`),
  getDetails: (id) => api.get(`/products/${id}/details`),
  create: (productData) => {
    // Se productData é FormData (tem imagem), usar multipart/form-data
    if (productData instanceof FormData) {
      return api.post("/products", productData, {
        headers: { "Content-Type": "multipart/form-data" }
      })
    }
    // Caso contrário, usar application/json normal
    return api.post("/products", productData)
  },
  update: (id, productData) => {
    // Se productData é FormData (tem imagem), usar multipart/form-data
    if (productData instanceof FormData) {
      return api.put(`/products/${id}`, productData, {
        headers: { "Content-Type": "multipart/form-data" }
      })
    }
    // Caso contrário, usar application/json normal
    return api.put(`/products/${id}`, productData)
  },
  delete: (id) => api.delete(`/products/${id}`),

  // Categorias
  getCategories: (filters = {}) => {
    const params = new URLSearchParams()

    // Filtro por busca
    if (filters.search) {
      params.append("search", filters.search)
    }

    // Paginação
    if (filters.page) {
      params.append("page", filters.page)
    }

    if (filters.limit) {
      params.append("limit", filters.limit)
    }

    // Ordenação
    if (filters.sortBy) {
      params.append("sortBy", filters.sortBy)
    }

    if (filters.sortOrder) {
      params.append("sortOrder", filters.sortOrder)
    }

    return api.get(`/products/categories/list?${params.toString()}`)
  },

  // Buscar todas as categorias sem paginação (para selects)
  getAllCategories: (search = "") => {
    const params = new URLSearchParams()

    if (search) {
      params.append("search", search)
    }

    return api.get(`/products/categories?${params.toString()}`)
  },
  getCategoryById: (id) => api.get(`/products/categories/${id}`),
  createCategory: (categoryData) =>
    api.post("/products/categories", categoryData),
  updateCategory: (id, categoryData) =>
    api.put(`/products/categories/${id}`, categoryData),
  deleteCategory: (id) => api.delete(`/products/categories/${id}`),

  // Busca e filtros
  search: (query, filters = {}) => {
    const params = new URLSearchParams()
    params.append("q", query)

    // Adicionar filtros extras na busca
    if (filters.categoryId) {
      params.append("categoryId", filters.categoryId)
    }

    if (filters.productType) {
      params.append("productType", filters.productType)
    }

    if (filters.productKind) {
      params.append("productKind", filters.productKind)
    }

    return api.get(`/products/search?${params.toString()}`)
  },

  // Filtros específicos
  getByCategory: (categoryId) => api.get(`/products/category/${categoryId}`),
  getByType: (productType) => api.get(`/products/type/${productType}`),
  getFeatured: () => api.get("/products/featured"),
  getRecommended: () => api.get("/products/recommended"),

  // Upload de imagem
  uploadImage: (productId, file) => {
    const formData = new FormData()
    formData.append("image", file)
    return api.post(`/products/${productId}/image`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
  },

  // Estatísticas
  getStats: () => api.get("/products/stats"),

  // Bulk operations
  bulkDelete: (productIds) =>
    api.post("/products/bulk-delete", { ids: productIds }),
  bulkUpdate: (updates) => api.post("/products/bulk-update", updates),

  // Exportar/Importar
  exportProducts: (filters = {}) =>
    api.get("/products/export", { params: filters }),
  importProducts: (file) => {
    const formData = new FormData()
    formData.append("file", file)
    return api.post("/products/import", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
  }
}

// Coupon Services (Cupons)
export const couponService = {
  // CRUD de cupons
  getAll: (filters = {}) => {
    const params = new URLSearchParams()
    if (filters.per_page) params.append("per_page", filters.per_page)
    if (filters.is_active !== undefined)
      params.append("is_active", filters.is_active)
    if (filters.type) params.append("type", filters.type)
    if (filters.search) params.append("search", filters.search)
    return api.get(`/coupons?${params.toString()}`)
  },
  getById: (id) => api.get(`/coupons/${id}`),
  create: (couponData) => api.post("/coupons", couponData),
  update: (id, couponData) => api.put(`/coupons/${id}`, couponData),
  toggleActive: (id) => api.patch(`/coupons/${id}/toggle-active`),
  delete: (id) => api.delete(`/coupons/${id}`),

  // Validação e aplicação
  validate: (validationData) => api.post("/coupons/validate", validationData),
  apply: (applicationData) => api.post("/coupons/apply", applicationData),

  // Ferramentas
  generateCode: () => api.get("/coupons/generate-code"),
  getStats: () => api.get("/coupons/stats")
}

// Patient Services
export const patientService = {
  getAll: (filters = {}) => api.get("/patients", { params: filters }),
  getById: (id) => api.get(`/patients/${id}`),
  create: (patientData) => api.post("/patients", patientData),
  update: (id, patientData) => api.put(`/patients/${id}`, patientData),
  delete: (id) => api.delete(`/patients/${id}`),
  search: (query) => api.get(`/patients/search?q=${query}`)
}

// Surgical Kit Services
export const surgicalKitService = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams()
    if (filters.search) params.append("search", filters.search)
    if (filters.all) params.append("all", filters.all)
    if (filters.per_page) params.append("per_page", filters.per_page)
    if (filters.page) params.append("page", filters.page)
    if (filters.sort_by) params.append("sort_by", filters.sort_by)
    if (filters.sort_order) params.append("sort_order", filters.sort_order)
    return api.get(`/surgical-kits?${params.toString()}`)
  },
  getById: (id) => api.get(`/surgical-kits/${id}`),
  create: (data) => api.post("/surgical-kits", data),
  update: (id, data) => api.put(`/surgical-kits/${id}`, data),
  delete: (id) => api.delete(`/surgical-kits/${id}`)
}

// Sleeve Services
export const sleeveService = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams()
    if (filters.search) params.append("search", filters.search)
    if (filters.color) params.append("color", filters.color)
    if (filters.all) params.append("all", filters.all)
    if (filters.per_page) params.append("per_page", filters.per_page)
    if (filters.page) params.append("page", filters.page)
    if (filters.sort_by) params.append("sort_by", filters.sort_by)
    if (filters.sort_order) params.append("sort_order", filters.sort_order)
    return api.get(`/sleeves?${params.toString()}`)
  },
  getById: (id) => api.get(`/sleeves/${id}`),
  create: (data) => api.post("/sleeves", data),
  update: (id, data) => api.put(`/sleeves/${id}`, data),
  delete: (id) => api.delete(`/sleeves/${id}`)
}

// Notification Services
export const notificationService = {
  // Listar notificações com filtros
  getAll: (filters = {}) => {
    const params = {
      per_page: filters.per_page || 20,
      ...filters
    }
    return api.get("/notifications", { params })
  },

  // Contador de notificações não lidas
  getUnreadCount: () => api.get("/notifications/unread-count"),

  // Marcar notificação específica como lida
  markAsRead: (id) => api.post(`/notifications/${id}/markAsRead`),

  // Marcar todas as notificações como lidas
  markAllAsRead: () => api.post("/notifications/markAllAsRead"),

  // Excluir notificação específica
  delete: (id) => api.delete(`/notifications/${id}`),

  // Buscar notificações não lidas para dropdown (10 itens)
  getUnreadForDropdown: () => {
    const params = {
      per_page: 10,
      read: false
    }
    return api.get("/notifications", { params })
  },

  // Buscar todas as notificações para página (15 itens com paginação)
  getAllForPage: (page = 1, filters = {}) => {
    const params = {
      per_page: 15,
      page,
      ...filters
    }
    return api.get("/notifications", { params })
  }
}

// Chat Services
export const chatService = {
  // Buscar mensagens do canal público (padrão)
  getMessages: (caseId, options = {}) => {
    const params = new URLSearchParams()
    if (options.limit) params.append("limit", options.limit)
    if (options.offset) params.append("offset", options.offset)
    const queryString = params.toString()
    return api.get(`/chat/${caseId}${queryString ? `?${queryString}` : ""}`)
  },

  // Buscar mensagens por canal específico
  getMessagesByChannel: (caseId, channel, options = {}) => {
    const params = new URLSearchParams()
    if (options.limit) params.append("limit", options.limit)
    if (options.offset) params.append("offset", options.offset)
    const queryString = params.toString()
    return api.get(
      `/chat/${caseId}/channel/${channel}${
        queryString ? `?${queryString}` : ""
      }`
    )
  },

  // Buscar mensagens por chat ID (novo método preferido)
  getMessagesByChatId: (caseId, chatId, options = {}) => {
    const params = new URLSearchParams()
    if (options.limit) params.append("limit", options.limit)
    if (options.offset) params.append("offset", options.offset)
    const queryString = params.toString()
    return api.get(
      `/chat/${caseId}/chat/${chatId}/messages${
        queryString ? `?${queryString}` : ""
      }`
    )
  },

  // Listar chats disponíveis (retorna objetos completos de chat)
  getAvailableChannels: (caseId) => api.get(`/chat/${caseId}/channels`),

  // Buscar chat específico por ID
  getChatById: (caseId, chatId) => api.get(`/chat/${caseId}/chat/${chatId}`),

  // Contadores de mensagens não lidas
  getUnreadCounts: (caseId) => api.get(`/chat/${caseId}/unread-counts`),

  // Estatísticas do chat
  getStats: (caseId) => api.get(`/chat/${caseId}/stats`),

  // Usuários do chat
  getUsers: (caseId) => api.get(`/chat/${caseId}/users`),

  // Enviar mensagem (nova estrutura)
  sendMessage: (caseId, messageData) => {
    return api.post(`/chat/${caseId}`, messageData)
  },

  // Métodos legados (mantidos para compatibilidade, se necessário)
  deleteMessage: (caseId, messageId) =>
    api.delete(`/chat/${caseId}/messages/${messageId}`),
  markAsRead: (caseId) => api.put(`/chat/${caseId}/mark-read`),
  uploadAttachment: (caseId, file) => {
    const formData = new FormData()
    formData.append("attachment", file)
    return api.post(`/chat/${caseId}/attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
  }
}

// Attachment Services
export const attachmentService = {
  upload: (file, type = "general") => {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("type", type)
    return api.post("/attachments/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
  },
  // Upload para casos (status NEW) - suporta arquivos até 10GB via presigned URL
  uploadSync: async (
    caseId,
    file,
    boxName,
    attachmentName,
    onProgress = null,
    caseServiceId = null
  ) => {
    // Step 1: Get presigned URL ou informações de multipart
    const uploadData = {
      case_id: caseId,
      filename: file.name,
      filesize: file.size,
      boxname: boxName,
      attachmentname: attachmentName
    }
    if (caseServiceId) uploadData.case_service_id = caseServiceId

    const presignedResponse = await api.post(
      "/attachment/uploadSync",
      uploadData
    )

    if (presignedResponse.data?.error !== false) {
      throw new Error(
        presignedResponse.data?.message || "Failed to get upload URL"
      )
    }

    const { attachment, s3, multipart } = presignedResponse.data.obj

    // Verificar se é upload multipart
    if (multipart && multipart.parts && multipart.parts.length > 0) {
      // Upload multipart - fazer upload de todas as partes EM PARALELO
      const totalParts = multipart.total_parts
      let completedParts = 0

      // Criar promises para upload de todas as partes em paralelo
      const uploadPromises = multipart.parts.map(async (part, index) => {
        const { part_number, url, start_byte, end_byte } = part

        // Extrair chunk do arquivo
        const chunk = file.slice(start_byte, end_byte + 1)

        // Upload da parte para S3 (sem Authorization header - URL já está pré-assinada)
        const partResponse = await axios.put(url, chunk, {
          headers: {
            "Content-Type": "application/octet-stream"
          },
          timeout: 30 * 60 * 1000,
          transformRequest: [
            (data, headers) => {
              // Remover header Authorization para uploads S3 com URL pré-assinada
              delete headers.Authorization
              return data
            }
          ],
          onUploadProgress: onProgress
            ? (progressEvent) => {
                // Calcular progresso geral baseado nas partes completas + progresso atual
                const partProgress = progressEvent.loaded / chunk.size
                const overallProgress =
                  ((completedParts + partProgress) / totalParts) * 100
                const totalUploaded =
                  completedParts * multipart.chunk_size + progressEvent.loaded
                onProgress(
                  Math.round(overallProgress),
                  totalUploaded,
                  file.size
                )
              }
            : undefined
        })

        // Incrementar contador de partes completas
        completedParts++

        // Capturar ETag (manter aspas duplas conforme retornado pelo S3)
        const etag = partResponse.headers.etag || partResponse.headers.ETag
        return {
          part_number,
          etag // Manter aspas duplas do S3
        }
      })

      // Aguardar todas as partes serem enviadas em paralelo
      const uploadedParts = await Promise.all(uploadPromises)

      // Completar upload multipart na URL correta
      const completeResponse = await api.post(
        `/attachment/uploadedSuccess/${attachment.id}`,
        {
          upload_id: multipart.upload_id,
          parts: uploadedParts
        }
      )

      if (completeResponse.data?.error !== false) {
        throw new Error(
          completeResponse.data?.message ||
            "Failed to complete multipart upload"
        )
      }

      return completeResponse
    } else if (s3 && s3.url) {
      // Upload single - código original
      // Step 2: Upload direto para S3 (progresso real aqui)
      // Headers para upload - s3.fields pode conter headers adicionais do backend
      const s3Headers = {
        "Content-Type": file.type || "application/octet-stream",
        ...(s3.headers || {}) // Usar headers do backend se fornecidos
      }

      const s3Response = await axios.put(s3.url, file, {
        headers: s3Headers,
        timeout: 30 * 60 * 1000, // 30 min para arquivos grandes
        transformRequest: [
          (data, headers) => {
            // Remover header Authorization para uploads S3 com URL pré-assinada
            delete headers.Authorization
            return data
          }
        ],
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              )
              onProgress(
                percentCompleted,
                progressEvent.loaded,
                progressEvent.total
              )
            }
          : undefined
      })

      if (!s3Response.status || s3Response.status >= 400) {
        throw new Error("Falha no upload para S3")
      }

      // Step 3: Confirmar upload no backend
      const confirmResponse = await api.get(
        `/attachment/uploadedSuccess/${attachment.id}`
      )

      if (confirmResponse.data?.error !== false) {
        throw new Error(
          confirmResponse.data?.message || "Falha ao confirmar upload"
        )
      }

      return confirmResponse
    } else {
      throw new Error("Invalid response structure from backend")
    }
  },
  // Listar anexos de um caso (NOVO - usa buckets com paginação e signed URLs em paralelo)
  listAttachments: (
    caseId,
    boxName = null,
    status = null,
    perPage = 15,
    caseServiceId = null
  ) => {
    // Se tem boxName, usar novo endpoint por bucket
    if (boxName) {
      const params = new URLSearchParams()
      params.append("page", "1")
      params.append("per_page", perPage.toString())
      if (caseServiceId) {
        params.append("case_service_id", caseServiceId.toString())
      }
      return api.get(`/cases/${caseId}/buckets/${encodeURIComponent(boxName)}?${params.toString()}`)
    }
    // Sem boxName, listar todos os buckets
    return api.get(`/cases/${caseId}/buckets`)
  },
  // NOVOS: Listar buckets de um caso
  getBuckets: (caseId) => {
    return api.get(`/cases/${caseId}/buckets`)
  },
  // NOVOS: Listar anexos de um bucket específico com paginação
  getBucketAttachments: (caseId, bucket, page = 1, perPage = 10) => {
    return api.get(`/cases/${caseId}/buckets/${encodeURIComponent(bucket)}?page=${page}&per_page=${perPage}`)
  },
  // Listar anexos de um pedido
  listOrderAttachments: (orderId) => {
    return api.get(`/attachment/orders/${orderId}`)
  },
  // Upload para pedidos (mesma lógica do uploadSync mas com order_id)
  uploadSyncForOrder: async (
    orderId,
    file,
    boxName,
    attachmentName,
    onProgress = null
  ) => {
    const presignedResponse = await api.post("/attachment/uploadSync", {
      order_id: orderId,
      filename: file.name,
      filesize: file.size,
      boxname: boxName,
      attachmentname: attachmentName
    })

    if (presignedResponse.data?.error !== false) {
      throw new Error(
        presignedResponse.data?.message || "Failed to get upload URL"
      )
    }

    const { attachment, s3, multipart } = presignedResponse.data.obj

    if (multipart && multipart.parts && multipart.parts.length > 0) {
      const totalParts = multipart.total_parts
      let completedParts = 0

      const uploadPromises = multipart.parts.map(async (part, index) => {
        const { part_number, url, start_byte, end_byte } = part
        const chunk = file.slice(start_byte, end_byte + 1)

        const partResponse = await axios.put(url, chunk, {
          headers: {
            "Content-Type": "application/octet-stream"
          },
          timeout: 30 * 60 * 1000,
          transformRequest: [
            (data, headers) => {
              delete headers.Authorization
              return data
            }
          ],
          onUploadProgress: onProgress
            ? (progressEvent) => {
                const partProgress = progressEvent.loaded / chunk.size
                const overallProgress =
                  ((completedParts + partProgress) / totalParts) * 100
                const totalUploaded =
                  completedParts * multipart.chunk_size + progressEvent.loaded
                onProgress(
                  Math.round(overallProgress),
                  totalUploaded,
                  file.size
                )
              }
            : undefined
        })

        completedParts++

        const etag = partResponse.headers.etag || partResponse.headers.ETag
        return {
          part_number,
          etag
        }
      })

      const uploadedParts = await Promise.all(uploadPromises)

      const completeResponse = await api.post(
        `/attachment/uploadedSuccess/${attachment.id}`,
        {
          upload_id: multipart.upload_id,
          parts: uploadedParts
        }
      )

      if (completeResponse.data?.error !== false) {
        throw new Error(
          completeResponse.data?.message ||
            "Failed to complete multipart upload"
        )
      }

      return completeResponse
    } else if (s3 && s3.url) {
      const s3Headers = {
        "Content-Type": file.type || "application/octet-stream",
        ...(s3.headers || {})
      }

      const s3Response = await axios.put(s3.url, file, {
        headers: s3Headers,
        timeout: 30 * 60 * 1000,
        transformRequest: [
          (data, headers) => {
            delete headers.Authorization
            return data
          }
        ],
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              )
              onProgress(
                percentCompleted,
                progressEvent.loaded,
                progressEvent.total
              )
            }
          : undefined
      })

      if (!s3Response.status || s3Response.status >= 400) {
        throw new Error("Falha no upload para S3")
      }

      const confirmResponse = await api.get(
        `/attachment/uploadedSuccess/${attachment.id}`
      )

      if (confirmResponse.data?.error !== false) {
        throw new Error(
          confirmResponse.data?.message || "Falha ao confirmar upload"
        )
      }

      return confirmResponse
    } else {
      throw new Error("Invalid response structure from backend")
    }
  },
  // Deletar anexo
  deleteAttachment: (attachmentId) =>
    api.delete(`/attachment/${attachmentId}/delete`),
  // Publicar anexo
  publishAttachment: (attachmentId) =>
    api.post(`/attachment/${attachmentId}/publish`),
  // Obter URL de download
  getDownloadUrl: (attachmentId) =>
    api.get(`/attachment/${attachmentId}/download`),
  // Métodos legados (mantidos para compatibilidade)
  delete: (id) => api.delete(`/attachments/${id}`),
  getById: (id) => api.get(`/attachments/${id}`)
}

// Geolocation Services
export const geolocationService = {
  calculateShipping: (cep, weight = 1.0) =>
    api.get(`/geolocation/shipping/${cep}?weight=${weight}`)
}

// Settings Services
export const settingsService = {
  getGeneralSettings: () => api.get("/settings/general"),
  updateGeneralSettings: (settings) => api.put("/settings/general", settings),
  getEmailSettings: () => api.get("/settings/email"),
  updateEmailSettings: (settings) => api.put("/settings/email", settings),
  getWhatsAppSettings: () => api.get("/settings/whatsapp"),
  updateWhatsAppSettings: (settings) => api.put("/settings/whatsapp", settings),
  getShipmentSettings: () => api.get("/settings/shipment"),
  updateShipmentSettings: (settings) => api.put("/settings/shipment", settings),
  testWhatsAppConnection: () => api.post("/settings/whatsapp/test-connection")
}

// Dashboard Services
export const dashboardService = {
  getAnalytics: (period = "30d") =>
    api.get(`/dashboard/analytics?period=${period}`),
  getCaseStats: () => api.get("/dashboard/cases/stats"),
  getRevenueStats: () => api.get("/dashboard/revenue/stats"),
  getUserStats: () => api.get("/dashboard/users/stats"),
  getRecentActivity: () => api.get("/dashboard/recent-activity")
}

// Shop Services
export const shopService = {
  // Catálogo público de produtos
  getCatalog: (filters = {}) => {
    const params = new URLSearchParams()

    if (filters.per_page) params.append("per_page", filters.per_page)
    if (filters.page) params.append("page", filters.page)
    if (filters.category_id) params.append("category_id", filters.category_id)
    if (filters.product_type)
      params.append("product_type", filters.product_type)
    if (filters.product_kind)
      params.append("product_kind", filters.product_kind)
    if (filters.search) params.append("search", filters.search)
    if (filters.sortBy) params.append("sort_by", filters.sortBy)

    const queryString = params.toString()
    // Usar api para enviar token e capturar 401
    return api.get(`/shop${queryString ? `?${queryString}` : ""}`)
  },

  // Buscar Drs para compra em nome de terceiro
  searchDoctors: (query) =>
    api.get(`/shop/search-doctors?q=${encodeURIComponent(query)}`)
}

// Cart Services
export const cartService = {
  // Visualizar carrinho
  getCart: () => api.get("/shop/cart"),

  // Adicionar produto ao carrinho (suporta snake_case e camelCase)
  addToCart: (data) => {
    const payload = {
      product_id: data.productId || data.product_id,
      quantity: data.quantity || 1
    }

    // Campos opcionais
    if (data.caseId || data.case_id) {
      payload.case_id = data.caseId || data.case_id
    }
    if (data.serviceData || data.service_data) {
      payload.service_data = data.serviceData || data.service_data
    }

    return api.post("/shop/addToCart", payload)
  },

  // Atualizar item do carrinho
  updateQuantity: (cartItemId, quantity) =>
    api.put(`/shop/cart/${cartItemId}`, { quantity }),

  updateBdsPrinting: (cartItemId, bdsWillPrint) => api.put(`/shop/cart/${cartItemId}`, { bds_will_print: bdsWillPrint }),

  // Remover item do carrinho
  removeFromCart: (cartItemId) => api.delete(`/shop/cart/${cartItemId}`),

  // Limpar carrinho
  clearCart: () => api.delete("/shop/cart"),

  // Aplicar cupom (se implementado no backend)
  applyCoupon: (couponCode) =>
    api.post("/shop/cart/coupon", { coupon_code: couponCode }),

  // Remover cupom (se implementado no backend)
  removeCoupon: () => api.delete("/shop/cart/coupon")
}

// Order Services
export const orderService = {
  // Finalizar compra (checkout)
  checkout: (checkoutData) => {
    const payload = {
      payment_method: checkoutData.payment_method,
      shipping_address: checkoutData.shipping_address
    }

    // Campos opcionais
    if (checkoutData.billing_data) {
      payload.billing_data = checkoutData.billing_data
    }
    if (checkoutData.coupon_code) {
      payload.coupon_code = checkoutData.coupon_code
    }
    if (checkoutData.notes) {
      payload.notes = checkoutData.notes
    }
    if (checkoutData.target_user_id) {
      payload.target_user_id = checkoutData.target_user_id
    }

    return api.post("/shop/checkout", payload)
  },

  // Listar pedidos do usuário
  getOrders: (filters = {}) => {
    console.log(
      "🌐 orderService.getOrders: Iniciando requisição com filtros:",
      filters
    )

    const params = new URLSearchParams()

    if (filters.per_page) params.append("per_page", filters.per_page)
    if (filters.page) params.append("page", filters.page)
    if (filters.status) params.append("status", filters.status)
    if (filters.search) params.append("search", filters.search)

    const queryString = params.toString()
    const url = `/shop/orders${queryString ? `?${queryString}` : ""}`

    console.log("🌐 orderService.getOrders: URL da requisição:", url)

    return api
      .get(url)
      .then((response) => {
        console.log("🌐 orderService.getOrders: Resposta recebida:", response)
        return response
      })
      .catch((error) => {
        console.error("🌐 orderService.getOrders: Erro na requisição:", error)
        throw error
      })
  },

  // Visualizar pedido específico
  getOrder: (orderId) => api.get(`/shop/orders/${orderId}`),

  // Forçar sincronização de status com PagBank (pix/pagseguro)
  refreshPaymentStatus: (orderId) =>
    api.post(`/shop/orders/${orderId}/refresh-payment`),

  // Aprovar pagamento do pedido (admin)
  approvePayment: (orderId) =>
    api.post(`/shop/orders/${orderId}/approve-payment`),

  // Marcar pedido como reembolsado (admin)
  markAsRefunded: (orderId) =>
    api.post(`/shop/orders/${orderId}/mark-refunded`)
}

export const notificationPreferenceService = {
  getAll: () => api.get("/notification-preferences"),
  update: (preferences) => api.put("/notification-preferences", { preferences }),
  updateCategory: (category, channelEmail, channelPush, enabled) =>
    api.put("/notification-preferences/category", {
      category,
      channel_email: channelEmail,
      channel_push: channelPush,
      enabled
    }),
  reset: () => api.post("/notification-preferences/reset")
}

// ============================================================
// RELATÓRIOS (Admin) - todos baseados em pedidos (shop_orders)
// ============================================================
const buildReportParams = (filters = {}) => {
  const params = new URLSearchParams()

  Object.entries(filters).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return
    if (Array.isArray(value)) {
      if (value.length === 0) return
      value.forEach((v) => {
        if (v !== null && v !== undefined && v !== "") {
          params.append(`${key}[]`, v)
        }
      })
    } else {
      params.append(key, value)
    }
  })

  return params.toString()
}

export const reportService = {
  getOverview: (filters = {}) =>
    api.get(`/reports/overview?${buildReportParams(filters)}`),
  getPayments: (filters = {}) =>
    api.get(`/reports/payments?${buildReportParams(filters)}`),
  getCoupons: (filters = {}) =>
    api.get(`/reports/coupons?${buildReportParams(filters)}`),
  getUsers: (filters = {}) =>
    api.get(`/reports/users?${buildReportParams(filters)}`),
  getTransactions: (filters = {}) =>
    api.get(`/reports/transactions?${buildReportParams(filters)}`)
}

export default api
